import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Stack
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const DeletionRequestModal = ({
  isOpen,
  onClose,
  requestData,
  onApprove,
  onReject
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleApprove = async () => {
    setIsProcessing(true);
    await onApprove(requestData);
    setIsProcessing(false);
  };

  const handleReject = async () => {
    setIsProcessing(true);
    await onReject(requestData);
    setIsProcessing(false);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isProcessing ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '10px'
        }
      }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <WarningAmberIcon sx={{ color: '#C94D34', fontSize: 32 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2C251E' }}>
            Account Deletion Request
          </Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#2C251E' }}>
            The following user has requested to delete their account:
          </Typography>
          
          <Box sx={{ 
            backgroundColor: '#FDF9F3', 
            padding: '16px', 
            borderRadius: '8px',
            border: '1px solid #E0D8C8',
            color: '#2C251E',
            fontWeight: 500
          }}>
            {(requestData?.firstName || requestData?.lastName) && (
              <Typography variant="body2" sx={{ mb: 1, color: '#2C251E !important' }}>
                <strong>Full Name:</strong> {`${requestData?.firstName || ''} ${requestData?.middleName ? requestData.middleName + ' ' : ''}${requestData?.lastName || ''}`.trim()}
              </Typography>
            )}
            {requestData?.age && (
              <Typography variant="body2" sx={{ mb: 1, color: '#2C251E !important' }}>
                <strong>Age:</strong> {requestData.age}
              </Typography>
            )}
            {requestData?.email && (
              <Typography variant="body2" sx={{ mb: 1, color: '#2C251E !important' }}>
                <strong>Email:</strong> {requestData.email}
              </Typography>
            )}
            {requestData?.contactNumber && (
              <Typography variant="body2" sx={{ mb: 1, color: '#2C251E !important' }}>
                <strong>Mobile Number:</strong> {requestData.contactNumber}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: '#2C251E !important' }}>
              <strong>Role:</strong> {requestData?.requestingUserRole || requestData?.role || 'N/A'}
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ mt: 2, color: '#9F4223', fontWeight: 500 }}>
            ⚠️ This action cannot be undone. All user data will be permanently deleted.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={onClose}
          disabled={isProcessing}
          sx={{
            color: '#59544A',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(89, 84, 74, 0.08)'
            }
          }}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleReject}
          disabled={isProcessing}
          variant="outlined"
          sx={{
            borderColor: '#59544A',
            color: '#59544A',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#3D3830',
              backgroundColor: 'rgba(89, 84, 74, 0.08)'
            }
          }}
        >
          {isProcessing ? <CircularProgress size={20} /> : 'Reject'}
        </Button>
        
        <Button
          onClick={handleApprove}
          disabled={isProcessing}
          variant="contained"
          sx={{
            backgroundColor: '#9F4223',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#7D3419'
            }
          }}
        >
          {isProcessing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Approve & Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletionRequestModal;
